import './InviteBonusFans.sass';

import React, { FC, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import copyToClipboard from 'copy-text-to-clipboard';

import { inviteSelector } from '$store/invite';
import { Block, Button } from '$uikit';
import {
  IcAngleRight16,
  IcCopy20,
  IcHelp28,
  IcNewTab28,
  IcUserPlus28,
  IcUsers28,
} from '$assets';
import { classNames, formatBigNumber, rnbr, statReachGoal } from '$utils';
import { Tooltip, Truncate } from '$shared/components';
import { useCurrentUserInfo, useTranslator, useWikiLink } from '$hooks';
import { showModal } from '$navigation/helper';
import { toastActions } from '$store/toast';

const CellButton: FC<{
  title: string;
  count: number;
  icon: any;
  chevron?: boolean;
  onClick?: any;
}> = (props) => {
  const { icon, title, count, chevron = true, onClick } = props;

  return (
    <div
      onClick={onClick}
      className={classNames({
        InviteBonusFans__button: true,
        inactive: !chevron,
      })}
    >
      <div className="InviteBonusFans__button__cont">
        <div className="InviteBonusFans__button__count_wrap">
          <div className="InviteBonusFans__button__count_icon">{icon}</div>
          <div className="InviteBonusFans__button__count">{formatBigNumber(count)}</div>
        </div>
        <div className="InviteBonusFans__button__title">{title}</div>
      </div>
      {chevron && (
        <div className="InviteBonusFans__button__chevron">
          <IcAngleRight16 />
        </div>
      )}
    </div>
  );
};

export const InviteBonusFans: FC<{ inviteLink: string }> = (props) => {
  const { invitedTotal, bonusFansAdded, bonusFansNeed, bonusMultiplier } =
    useSelector(inviteSelector);

  const t = useTranslator();
  const wikiLink = useWikiLink('/for-creators/bonus-traffic');
  const user = useCurrentUserInfo();
  const dispatch = useDispatch();

  const handleCopy = useCallback(() => {
    copyToClipboard(props.inviteLink);
    dispatch(toastActions.setToastSuccess(t('copied')));

    if (user.verified === 2) {
      statReachGoal('invite_link_copied');
    }
  }, [dispatch, t, props.inviteLink, user.verified]);

  return (
    <Block className="InviteBonusFans">
      <div className="InviteBonusFans__head">
        <div className="InviteBonusFans__title">
          {t('bonus_fans_title')}
          <span>x{bonusMultiplier}</span>
        </div>
        <Tooltip
          iconShown={false}
          title={t('bonus_fans_tip_title')}
          text={rnbr(t('bonus_fans_tip_text'))}
          action={
            <Button stretched iconRight={<IcNewTab28 />} target="_blank" href={wikiLink}>
              {t('read_more')}
            </Button>
          }
        >
          <div className="InviteBonusFans__info">
            <IcHelp28 />
          </div>
        </Tooltip>
      </div>
      <div className="InviteBonusFans__caption">
        <Truncate
          lines={8}
          limit={200}
          text={t('bonus_fans_caption', {
            multiplier: bonusMultiplier,
          })}
          expandText={t('post_text_expand')}
        />
      </div>
      <div className="InviteBonusFans__actions">
        <div className="InviteBonusFans__actions_row">
          <Tooltip
            iconShown={false}
            title={t('bonus_fans_invited_tip_title')}
            text={rnbr(t('bonus_fans_invited_tip_text'))}
          >
            <CellButton
              title={t('bonus_fans_invited')}
              count={invitedTotal}
              icon={<IcUserPlus28 />}
            />
          </Tooltip>
          <Tooltip
            iconShown={false}
            title={t('bonus_fans_will_get_tip_title')}
            text={rnbr(t('bonus_fans_will_get_tip_text'))}
          >
            <CellButton
              title={t('bonus_fans_will_get')}
              count={bonusFansNeed}
              icon={<IcUsers28 />}
            />
          </Tooltip>
        </div>
        <div className="InviteBonusFans__actions_row">
          <CellButton
            title={t('bonus_fans_got_fans')}
            count={bonusFansAdded}
            icon={<IcUserPlus28 />}
            onClick={() => showModal('bonus_fans')}
          />
          <CellButton
            title={t('bonus_fans_referrals')}
            count={invitedTotal}
            icon={<IcUsers28 />}
            onClick={() => showModal('bonus_fans_referrals')}
          />
        </div>
      </div>
      {/*<div className="InviteBonusFans__link_wrap" onClick={handleCopy}>*/}
      {/*  <div className="InviteBonusFans__link">*/}
      {/*    {t('invite_link')} <span>{props.inviteLink}</span>*/}
      {/*  </div>*/}
      {/*  <Button size="medium" icon={<IcCopy20 />}>*/}
      {/*    {t('invite_link_copy')}*/}
      {/*  </Button>*/}
      {/*</div>*/}
    </Block>
  );
};
